import {AdManager} from "./AdManager";
import {IEvent, IGame, ILinkListAd, IScheduledAd} from "@webng-types/write-model";
import React, {ReactNode, useEffect, useMemo, useState} from "react";
import {EventExtensionComponent, EventExtensionProps} from "@webng/liveblog/src";
import {InlineAdView} from "./InlineAdView";
import {LinkListAdManager} from "./LinkListAdManager";
import {ScheduledAdManagerV2} from "./ScheduledAdManagerV2";

export function useAdExtension(adManager: AdManager|undefined, rows: IEvent[], game?: IGame) {
  // Check if the game has ad_integrations
  const adIntegrations = game?.ad_integrations;

  // Create ad managers for each ad integration
  const adManagers = useMemo(() => {
    const managers: AdManager[] = [];

    // Add the provided adManager if it exists
    if (adManager) {
      managers.push(adManager);
    }

    // Add managers for ad_integrations
    adIntegrations?.forEach(adIntegration => {
      if (!adIntegration.enabled) {
        return; // Skip disabled ad integrations
      }

      if (adIntegration._type === "Tik::Model::ScheduledAd") {
        const scheduledAd = adIntegration as IScheduledAd;
        managers.push(new ScheduledAdManagerV2(
          'AboveEvent',
          scheduledAd.start,
          scheduledAd.interval,
          scheduledAd.html
        ));
      } else if (adIntegration._type === "Tik::Model::LinkListAd") {
        const linkListAd = adIntegration as ILinkListAd;
        managers.push(new LinkListAdManager(
          'AboveEvent', // Default position
          linkListAd
        ));
      }
    });

    return managers;
  }, [adManager, adIntegrations]);

  // Update all ad managers when rows change
  useEffect(() => {
    adManagers.forEach(manager => {
      manager.update(rows);
    });
  }, [adManagers, rows]);

  // Create extensions for all ad managers
  return useMemo(() => {
    const extensions: Record<string, EventExtensionComponent[]> = {};

    adManagers.forEach(manager => {
      const AdExtension: EventExtensionComponent = ({event}: EventExtensionProps) => {
        const [html, setHtml] = useState<ReactNode|string>();

        useEffect(() => {
          return manager.subscribe(event, setHtml);
        }, [event]);

        if (html && typeof html === 'string') {
          return <InlineAdView html={html} position={manager.position}/>;
        } else if(html) {
          return html
        } else {
          return null;
        }
      };

      extensions[manager.position] = extensions[manager.position] ? [...extensions[manager.position], AdExtension] : [AdExtension];
    });

    return extensions;
  }, [adManagers]);
}
